/**
 * Common Dashboard Framework API client (/api/dashboard/v2).
 *
 * Auth is injected globally by the axios request interceptor in App.js, so
 * these helpers never touch tokens. `API` already includes the `/api` prefix.
 */
import axios from 'axios';

// Resolve the API base from the environment (identical to App.js's
// `const API = `${REACT_APP_BACKEND_URL}/api``). We deliberately do NOT import
// { API } from '@/App': this module is pulled in via @/dashboard from App.js
// itself, so importing back would be circular and reading `API` during that
// cycle throws "can't access lexical declaration 'API' before initialization"
// (temporal dead zone). Deriving it locally breaks the cycle entirely.
const base = () => `${process.env.REACT_APP_BACKEND_URL}/api/dashboard/v2`;

/** Time-filter presets for the FilterBar. Returns [{ id, label }]. */
export async function fetchTimePresets() {
  const { data } = await axios.get(`${base()}/presets`);
  return data?.presets || [];
}

/** Metric descriptors (metadata only) for a module. Returns [{ key, label, ... }]. */
export async function fetchMetricCatalog(module) {
  const { data } = await axios.get(`${base()}/catalog`, {
    params: module ? { module } : {},
  });
  return data?.metrics || [];
}

/**
 * Compute a set of KPIs.
 * body: { module, metrics:[keys], site_id?, department_id?, time:{preset|custom}, fy_start_month? }
 * Returns { module, site_id, range, metrics:{key:{value,...}}, unauthorized:[], invalid:[] }.
 */
export async function fetchKpis(body, { signal } = {}) {
  const { data } = await axios.post(`${base()}/kpis`, body, { signal });
  return data;
}

/**
 * List the records behind a KPI (paged/sorted/searchable).
 * body: { metric, site_id?, department_id?, time?, page, page_size, sort?, sort_dir?, search? }
 * Returns { metric, columns:[{key,label}], rows:[...], total, page, page_size, sort, sort_dir }.
 */
export async function fetchDrilldown(body, { signal } = {}) {
  const { data } = await axios.post(`${base()}/drilldown`, body, { signal });
  return data;
}

/**
 * Year-on-year monthly series for one metric.
 * body: { module, metric, site_id?, dimensions?, years?|num_years }
 * Returns a ChartWidget-ready payload { label, unit, format, xKey:'month', data, series }.
 */
export async function fetchYoY(body, { signal } = {}) {
  const { data } = await axios.post(`${base()}/yoy`, body, { signal });
  return data;
}

// ---- Per-user dashboard layout (arrangement of predefined widgets) ----

export async function getLayout(dashboardKey, { signal } = {}) {
  const { data } = await axios.get(`${base()}/layout`, {
    params: { dashboard_key: dashboardKey }, signal,
  });
  return data?.layout || null; // null → use the page default
}

export async function saveLayout(dashboardKey, layout, { signal } = {}) {
  const { data } = await axios.put(`${base()}/layout`, { dashboard_key: dashboardKey, layout }, { signal });
  return data;
}

export async function resetLayout(dashboardKey, { signal } = {}) {
  const { data } = await axios.delete(`${base()}/layout`, {
    params: { dashboard_key: dashboardKey }, signal,
  });
  return data;
}

/**
 * Break a metric down by a dimension (group-by). body: { module, metric,
 * group_by, site_id?, dimensions?, time?, limit }.
 * Returns { group_by, xKey:'label', series, data:[{key,label,value}] }.
 */
export async function fetchBreakdown(body, { signal } = {}) {
  const { data } = await axios.post(`${base()}/breakdown`, body, { signal });
  return data;
}

/**
 * Monthly series for one or more COUNT metrics on a shared axis.
 * body: { module, metrics:[keys], months?|time?, site_id?, dimensions? }.
 * Returns { xKey:'period', series:[{key:'m0',label,metric}], data:[{period,m0,…}] }.
 * Series keys are aliased (m0/m1/…) because recharts reads a dot in a dataKey
 * as a nested path.
 */
export async function fetchMetricSeries(body, { signal } = {}) {
  const { data } = await axios.post(`${base()}/series`, body, { signal });
  return data;
}

/**
 * Top root-cause categories behind incidents in scope (from the app's own
 * root-cause category tags). body: { site_id?, dimensions?, time?, limit }.
 * Returns { data:[{key,label,value}], total_causes, truncated, coverage, drill }.
 */
export async function fetchIncidentCauses(body, { signal } = {}) {
  const { data } = await axios.post(`${base()}/safety/causes`, body, { signal });
  return data;
}

/**
 * Locations ranked by assessed risk + incident history.
 * Returns { data:[{label,value,high_risk_hazards,max_risk_score,incidents,…}], basis }.
 */
export async function fetchHighRiskLocations(body, { signal } = {}) {
  const { data } = await axios.post(`${base()}/safety/high-risk-locations`, body, { signal });
  return data;
}

/**
 * Incident groups that recur on the same composite key.
 * body: { group_keys?:[...], min_count?, limit?, site_id?, dimensions?, time? }.
 * Returns { groups:[{label,parts,filters,occurrences,open_count,with_capa,…}],
 *           data, available_keys, min_count, excludes_untagged }.
 */
export async function fetchRepeatIncidents(body, { signal } = {}) {
  const { data } = await axios.post(`${base()}/safety/repeat-incidents`, body, { signal });
  return data;
}

/**
 * Open hazards across the tenant's configured 5x5 HIRA risk matrix.
 * body: { site_id?, dimensions?, basis:'inherent'|'residual' }.
 * Returns { cells:[{severity,likelihood,score,count,band,color}], bands, total,
 *           unscored, drill }.
 */
export async function fetchRiskMatrix(body, { signal } = {}) {
  const { data } = await axios.post(`${base()}/safety/risk-matrix`, body, { signal });
  return data;
}

/** Per-site comparison table. body: { module, metrics, site_ids?, time?, dimensions? }. */
export async function fetchComparison(body, { signal } = {}) {
  const { data } = await axios.post(`${base()}/comparison`, body, { signal });
  return data;
}

/** Selectable values for a dimension filter. Returns [{ value, label }]. */
export async function fetchFilterOptions(module, dimension, { signal } = {}) {
  const { data } = await axios.get(`${base()}/filter-options`, {
    params: { module, dimension },
    signal,
  });
  return data?.options || [];
}

// ---- Saved filters (named, reusable filter sets per user + module) ----

export async function listSavedFilters(module, { signal } = {}) {
  const { data } = await axios.get(`${base()}/filters`, { params: { module }, signal });
  return data?.filters || [];
}

export async function saveFilter(body, { signal } = {}) {
  // body: { module, name, filters, is_shared }
  const { data } = await axios.post(`${base()}/filters`, body, { signal });
  return data;
}

export async function updateSavedFilter(id, body, { signal } = {}) {
  const { data } = await axios.put(`${base()}/filters/${id}`, body, { signal });
  return data;
}

export async function deleteSavedFilter(id, { signal } = {}) {
  const { data } = await axios.delete(`${base()}/filters/${id}`, { signal });
  return data;
}

/**
 * Export a KPI snapshot. body: { module, metrics, site_id?, department_id?,
 * time?, fy_start_month?, format: 'csv'|'excel'|'pdf' }.
 * Returns { blob, filename } for a client-side download.
 */
export async function exportDashboard(body) {
  const resp = await axios.post(`${base()}/export`, body, { responseType: 'blob' });
  const cd = resp.headers['content-disposition'] || '';
  const match = /filename="?([^"]+)"?/.exec(cd);
  const ext = body.format === 'excel' ? 'xlsx' : body.format;
  const filename = match ? match[1] : `${body.module || 'dashboard'}.${ext}`;
  return { blob: resp.data, filename };
}

/**
 * Training assigned vs completed, grouped by a course attribute.
 * body: { group_by:'topic'|'category'|'training_type'|'department',
 *         site_id?, dimensions?, time?, limit? }.
 * Returns { data:[{label,Completed,Outstanding,assigned}], series, stacked,
 *           axis_label, totals, coverage, truncated, drill }.
 */
export async function fetchTrainingBreakdown(body, { signal } = {}) {
  const { data } = await axios.post(`${base()}/ehs/training-breakdown`, body, { signal });
  return data;
}

/**
 * Per-requirement legal compliance state for the site, worst first.
 * body: { site_id?, dimensions?, limit? }.
 * Returns { data:[{title,citation,category,owner,due_date,status,status_label}],
 *           columns, counts, applicable, judged, met, compliance_rate }.
 * compliance_rate is null (not 0) when nothing has come due yet.
 */
export async function fetchComplianceRegister(body, { signal } = {}) {
  const { data } = await axios.post(`${base()}/ehs/compliance-register`, body, { signal });
  return data;
}
